import type { MessageDirection } from '@/constants';

// CloudEvent Protocol Types
export interface CloudEvent<T = unknown> {
  specversion: string;
  type: string;
  source: string;
  id: string;
  time: string;
  datacontenttype: string;
  subject?: string;
  data: T;
}

// CloudEvent Type Constants
export const CLOUD_EVENT_TYPES = {
  CONNECTION_ESTABLISHED: 'com.cuca-gazelle.connection.established',
  GENERATING_RESPONSE: 'com.cuca-gazelle.generating-response',
  RESPONSE: 'com.cuca-gazelle.response',
  STATUS_UPDATE: 'com.cuca-gazelle.status.update',
  ERROR: 'com.cuca-gazelle.error',
  EVENT_PROCESSED: 'com.cuca-gazelle.event.processed',
  TRANSLATE: 'com.cuca-gazelle.translate',
  GENERATING_TRANSLATION: 'com.cuca-gazelle.generating-translation',
  TRANSLATE_RESPONSE: 'com.cuca-gazelle.translate.response',
  SUMMARY: 'com.cuca-gazelle.summary',
  GENERATING_SUMMARY: 'com.cuca-gazelle.generating-summary',
  SUMMARY_RESPONSE: 'com.cuca-gazelle.summary.response',
  SUMMARY_ERROR: 'com.cuca-gazelle.summary.error',
  SIMILARITY: 'com.cuca-gazelle.similarity',
  GENERATING_SIMILARITY: 'com.cuca-gazelle.generating-similarity',
  SIMILARITY_RESPONSE: 'com.cuca-gazelle.similarity.response',
  SIMILARITY_ERROR: 'com.cuca-gazelle.similarity.error',
} as const;

// Base CloudEvent data interface with common fields
export interface BaseEventData {
  // Common fields that might appear across different event types
  timestamp?: string;
  event_count?: number;
  [key: string]: unknown;
}

// Specific CloudEvent Data Types
export interface ConnectionEventData extends BaseEventData {
  // Connection state
  status?: string;
  client_count?: number;
  session_restored?: boolean;

  // Session data included when restoring connection
  alarm_scheduled?: boolean;
  alarm_time?: string | null;
  pending_messages?: number;
  has_pending_messages?: boolean;
  last_event_time?: string | null;
}

export interface ProcessingEventData extends BaseEventData {
  // Processing state
  message_count?: number;
  session_id?: string;
  timeout_seconds?: number;
}

export interface ErrorEventData extends BaseEventData {
  // Error information
  error_type?: string;
  error_message?: string;
}

export interface StatusUpdateEventData extends BaseEventData {
  // Legacy status updates
  status?: string;
}

export interface EventProcessedData extends BaseEventData {
  // Event acknowledgment
  status?: string;
  topic?: string;
}

// Translation request data
export interface TranslationRequestData {
  text: string;
  target: string;
}

// Translation response data
export interface TranslationResponseData extends BaseEventData {
  data: string;
}

// Translation status data
export interface TranslationStatusData extends BaseEventData {
  status?: string;
}

// Summary request data
export type SummaryRequestData = BaseEventData;

// Summary status data
export interface SummaryStatusData extends BaseEventData {
  status?: string;
  session_id?: string;
}

// Summary response data
export interface SummaryResponseData extends BaseEventData {
  summary: string;
  session_id?: string;
}

// Summary error data
export interface SummaryErrorData extends BaseEventData {
  error: string;
  session_id?: string;
}

// Similarity request data
export interface SimilarityRequestData {
  text: string;
  text_2: string;
}

// Similarity status data
export interface SimilarityStatusData extends BaseEventData {
  status?: string;
}

// Similarity response data
export interface SimilarityResponseData extends BaseEventData {
  similarity_score: number;
  sessionId?: string;
}

// Similarity error data
export interface SimilarityErrorData extends BaseEventData {
  error: string;
  sessionId?: string;
}

// For responses that come in format/response structure
export interface FormattedResponseData extends BaseEventData {
  format?: string;
  response?: string;
}

// Flexible type that can handle any event data structure
// This allows us to access any property safely with optional chaining
export interface BackendResponseData extends BaseEventData {
  // Connection events
  status?: string;
  client_count?: number;
  session_restored?: boolean;

  // Session restoration data
  alarm_scheduled?: boolean;
  alarm_time?: string | null;
  pending_messages?: number;
  has_pending_messages?: boolean;
  last_event_time?: string | null;

  // Processing events
  message_count?: number;
  session_id?: string;
  timeout_seconds?: number;

  // Error events
  error_type?: string;
  error_message?: string;

  // Event processed
  topic?: string;

  // Response events (includes BackendResponse fields)
  answer?: string;
  answer_nl?: string;
  input_language?: string;
  question?: string;
  question_nl?: string;
  bike_info?: {
    model: string;
    sku: string;
    snippet: string;
  };
  sources?: Array<{
    document_id: string;
    score: number;
    snippet: string;
    source: string;
  }>;
  articles?: Array<{
    category: string;
    link: string;
    score: number;
    summary: string;
    title: string;
  }>;

  // Format/response scenario
  format?: string;
  response?: string;

  // Summary response
  summary?: string;

  // Error handling
  error?: string;
}

// Specific CloudEvent Types
export type GenesysCloudEvent = CloudEvent<GenesysNotification>;
export type BackendCloudEvent = CloudEvent<BackendResponseData>;

// Type-specific CloudEvent types for better type safety (when needed)
export type ConnectionCloudEvent = CloudEvent<ConnectionEventData>;
export type ProcessingCloudEvent = CloudEvent<ProcessingEventData>;
export type ResponseCloudEvent = CloudEvent<BackendResponse>;
export type ErrorCloudEvent = CloudEvent<ErrorEventData>;
export type StatusUpdateCloudEvent = CloudEvent<StatusUpdateEventData>;
export type TranslationRequestCloudEvent = CloudEvent<TranslationRequestData>;
export type TranslationResponseCloudEvent = CloudEvent<TranslationResponseData>;
export type TranslationStatusCloudEvent = CloudEvent<TranslationStatusData>;
export type SummaryRequestCloudEvent = CloudEvent<SummaryRequestData>;
export type SummaryResponseCloudEvent = CloudEvent<SummaryResponseData>;
export type SummaryStatusCloudEvent = CloudEvent<SummaryStatusData>;
export type SummaryErrorCloudEvent = CloudEvent<SummaryErrorData>;
export type SimilarityRequestCloudEvent = CloudEvent<SimilarityRequestData>;
export type SimilarityResponseCloudEvent = CloudEvent<SimilarityResponseData>;
export type SimilarityStatusCloudEvent = CloudEvent<SimilarityStatusData>;
export type SimilarityErrorCloudEvent = CloudEvent<SimilarityErrorData>;

// Additional Information Structure
export interface AdditionalInfo {
  bicycle_details?: Record<string, unknown>;
  related_articles?: Array<{
    title: string;
    summary: string;
    link: string;
    category?: string;
    score?: number;
    source?: string; // Source name from the backend
  }>;
  suggestions?: string[];
}

// Genesys Integration Types
export interface GenesysNotification {
  topicName: string;
  eventTime: string;
  eventBody: {
    id: string;
    conversationId?: string;
    timestamp?: string;
    mediaType?: string;
    body?: string;
    direction?: MessageDirection;
    from?: {
      id: string;
      name?: string;
    };
    to?: {
      id: string;
      name?: string;
    };
    messengerType?: string;
  };
}

// Backend Communication Types
export interface BackendResponse {
  // AI response content
  answer?: string;
  answer_nl?: string;
  input_language?: string;
  question?: string;
  question_nl?: string;

  // Additional content
  bike_info?: {
    model: string;
    sku: string;
    snippet: string;
  };
  sources?: Array<{
    document_id: string;
    score: number;
    snippet: string;
    source: string;
  }>;
  articles?: {
    category: string;
    link: string;
    score: number;
    summary: string;
    title: string;
  }[];

  // Legacy fields
  translation?: string;
  original_message?: string;
  additional_info?: AdditionalInfo;

  // Error handling
  error?: string;

  // Format/response scenario - when backend returns data in this structure
  format?: string;
  response?: string;
}

export interface SessionStatus {
  // Basic Event Tracking
  event_count?: number;
  last_event_time?: string | null;

  // Alarm System (AI Response Trigger)
  alarm_scheduled?: boolean;
  alarm_time?: string | null;

  // WebSocket Connection State
  pending_messages?: number;
  has_pending_messages?: boolean;
}
